import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed, faBath, faCar, faRuler, faHouse,
  faLocationDot, faPercent, faAngleLeft, faAngleRight
} from '@fortawesome/free-solid-svg-icons';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { motion, AnimatePresence } from 'framer-motion';
import { Transition } from '@headlessui/react';
import MoveInWithUs from './MoveInWithUs'

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Add this helper function at the top of the file
const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// Add these helper functions after the BASE_URL constant
const buildImageUrl = (homeId, filename) => {
  return `${BASE_URL}/api/homes/${homeId}/images/${filename}`;
};

const buildFileUrl = (homeId, filename) => {
  return `${BASE_URL}/api/homes/${homeId}/files/${filename}`;
};

const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

// Add this helper function to extract values from array of objects
const extractValues = (arr) => {
  if (!arr) return [];
  return arr.map(item => typeof item === 'object' ? item.value : item);
};

const VideoModal = ({ videoId, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 p-4 md:p-8">
      <div className="absolute top-6 right-6">
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="h-full max-h-[85vh] max-w-6xl mx-auto mt-8">
        <div className="relative pb-[56.25%] h-0">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="Virtual Tour"
            className="absolute top-0 left-0 w-full h-full"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    </div>
  );
};

const ImageGallery = ({ images, activeImage, setActiveImage, nextImage, prevImage, home }) => (
  <div className="relative h-[85vh] bg-gray-900">
    <div className="absolute inset-0">
      <motion.img
        key={activeImage}
        src={images[activeImage]}
        alt={`Property view ${activeImage + 1}`}
        className="h-full w-auto object-contain mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      />
    </div>

    {/* Navigation Dots */}
    <div className="absolute bottom-8 left-0 right-0">
      <div className="flex justify-center gap-2">
        {images.map((_, idx) => (
          <button
            key={idx}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === activeImage ? 'bg-white w-4' : 'bg-white/50'
            }`}
            onClick={() => setActiveImage(idx)}
          />
        ))}
      </div>
    </div>

    {/* Navigation Arrows */}
    <button
      onClick={prevImage}
      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 
        text-white p-4 rounded-full transition-all"
    >
      <FontAwesomeIcon icon={faAngleLeft} size="lg" />
    </button>
    <button
      onClick={nextImage}
      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 
        text-white p-4 rounded-full transition-all"
    >
      <FontAwesomeIcon icon={faAngleRight} size="lg" />
    </button>
  </div>
);

const PropertyOverview = ({ home }) => (
  <div className="bg-white shadow-lg -mt-20 relative z-10 rounded-t-3xl">
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Left Column - Main Details */}
        <div className="space-y-6">
          <div>
            <h1 className="text-4xl font-bold text-primary">{home.propertyName}</h1>
            <div className="flex items-center gap-2 mt-2 text-gray-600">
              <FontAwesomeIcon icon={faLocationDot} />
              <p>{home.streetAddress}, {home.state}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-primary">
                <FontAwesomeIcon icon={faRuler} />
                <span className="text-lg font-semibold">{home.area}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Total Area</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-primary">
                <FontAwesomeIcon icon={faPercent} />
                <span className="text-lg font-semibold">{home.price}%</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Rent</p>
            </div>
          </div>
        </div>

        {/* Right Column - Key Features */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <FontAwesomeIcon icon={faBed} className="text-primary text-xl" />
            <div>
              <p className="font-semibold">{home.bedrooms + (home.guestRooms ? ` + ${home.guestRooms}` : '')}</p>
              <p className="text-sm text-gray-600">Bedrooms</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <FontAwesomeIcon icon={faBath} className="text-primary text-xl" />
            <div>
              <p className="font-semibold">{home.bathrooms}</p>
              <p className="text-sm text-gray-600">Bathrooms</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <FontAwesomeIcon icon={faCar} className="text-primary text-xl" />
            <div>
              <p className="font-semibold">{home.garage}</p>
              <p className="text-sm text-gray-600">Garage</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <FontAwesomeIcon icon={faHouse} className="text-primary text-xl" />
            <div>
              <p className="font-semibold">{home.building}</p>
              <p className="text-sm text-gray-600">Building Type</p>
            </div>
          </div>
        </div>
      </div>

      {/* SDA Info */}
      {home.isRegisteredSda && (
        <div className="mt-8 p-4 bg-primary/10 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="bg-primary text-white p-3 rounded-full">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-primary">SDA Registered Property</h3>
              <p className="text-gray-600">Category: {home.sdaCategory}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

const DocumentsSection = ({ home, id, setIsVideoModalOpen }) => (
  home.floorPlanPdf && home.propertyFlyerPdf && home.youtubeUrl && (
  <div className="bg-primary text-white py-16">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        {home.youtubeUrl && (
          <div 
            onClick={() => setIsVideoModalOpen(true)}
            className="relative aspect-video cursor-pointer group"
          >
            <img
              src={`https://img.youtube.com/vi/${getYouTubeId(home.youtubeUrl)}/maxresdefault.jpg`}
              alt="Virtual Tour"
              className="w-full h-full object-cover rounded-xl"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center
                group-hover:scale-110 transition-transform">
                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 5v10l8-5-8-5z"/>
                </svg>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-8">
          <h2 className="text-3xl font-bold">Property Documents</h2>
          <div className="space-y-4">
            {home.floorPlanPdf && (
              <a 
                href={buildFileUrl(id, home.floorPlanPdf)}
                download
                className="flex items-center gap-4 p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <div className="p-3 bg-primary rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium">Floor Plan</h3>
                  <p className="text-sm text-white/70">Download PDF</p>
                </div>
              </a>
            )}
            {home.propertyFlyerPdf && (
              <a 
                href={buildFileUrl(id, home.propertyFlyerPdf)}
                download
                className="flex items-center gap-4 p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <div className="p-3 bg-primary rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium">Property Flyer</h3>
                  <p className="text-sm text-white/70">Download PDF</p>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  )
);

const PropertySpecifications = ({ home }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {Object.entries({
      'Access Features': home.access,
      'Environment': home.environment,
      'Outside Features': home.outside,
      'Stay Duration': home.stayDuration,
      'Shared Living': home.sharedLiving
    }).map(([title, items]) => items?.length > 0 && (
      <motion.div
        key={title}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className=" rounded-lg p-6"
      >
        <h3 className="text-xl font-semibold mb-4 text-primary">{title}</h3>
        <ul className="space-y-3">
          {extractValues(items).map((item, index) => (
            <li key={index} className="flex items-center gap-2 text-gray-700">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              {item}
            </li>
          ))}
        </ul>
      </motion.div>
    ))}
  </div>
);

const ViewHome = () => {
  const { id } = useParams();
  const [home, setHome] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const quillContainer = useRef(null);
  const quillInstance = useRef(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  useEffect(() => {
    const fetchHome = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/homes/${id}`);
        const data = await response.json();
        setHome(data);

        // Delay Quill initialization to ensure container is ready
        setTimeout(() => {
          if (data && !quillInstance.current && quillContainer.current) {
            quillInstance.current = new Quill(quillContainer.current, {
              readOnly: true,
              theme: 'snow',
              modules: {
                toolbar: false
              }
            });
            
            // Handle both Delta and HTML content
            try {
              const content = JSON.parse(data.about);
              quillInstance.current.setContents(content);
            } catch (e) {
              // If parsing fails, assume it's HTML content
              quillInstance.current.root.innerHTML = data.about || '';
            }
          }
        }, 100);
      } catch (error) {
        console.error('Error fetching home:', error);
      }
    };
    fetchHome();

    // Cleanup
    return () => {
      if (quillInstance.current) {
        // Remove toolbar if exists
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar) {
          toolbar.remove();
        }
      }
    };
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!home) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
    </div>
  );

  const images = Array.isArray(home?.images) 
    ? home.images.map(img => buildImageUrl(id, img))
    : home?.images?.split(',').map(img => buildImageUrl(id, img.trim())) || [];

  const nextImage = () => {
    setActiveImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setActiveImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen">

<DocumentsSection 
              home={home} 
              id={id} 
              setIsVideoModalOpen={setIsVideoModalOpen}
            />

      <ImageGallery
        images={images}
        activeImage={activeImage}
        setActiveImage={setActiveImage}
        nextImage={nextImage}
        prevImage={prevImage}
        home={home}
      />
      
      <PropertyOverview home={home} />

      <div className="">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="space-y-16">
           

            {/* About Section */}
            <section>
              <h2 className="text-3xl font-bold mb-8">About This Property</h2>
              <div className="prose max-w-none">
                <div ref={quillContainer} />
              </div>
            </section>

            {/* Features Section */}
            <section>
              <h2 className="text-3xl font-bold mb-8">Specifications</h2>
              <PropertySpecifications home={home} />
            </section>

            {/* Location Section */}
            <section>
              <h2 className="text-3xl font-bold mb-8">Location</h2>
              <div className="h-[500px] rounded-xl overflow-hidden">
                <MapContainer
                  center={[home?.lat || 0, home?.long || 0]}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <Marker position={[home?.lat || 0, home?.long || 0]}>
                    <Popup>{home?.streetAddress}</Popup>
                  </Marker>
                </MapContainer>
              </div>
            </section>
          </div>
        </div>
      </div>

      <VideoModal
        videoId={getYouTubeId(home?.youtubeUrl)}
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
      />

      <MoveInWithUs home={home.propertyName} />
    </div>
  );
};

export default ViewHome;
